import React, { useState, useEffect, useRef } from 'react';
import OtpInputGroup from './OtpInputGroup';
import { apiRequest } from '../../../services/api';

function maskEmail(email) {
  if (!email || !email.includes('@')) { return email || ''; }
  const [local, domain] = email.split('@');
  return `${local.slice(0, 2)}***@${domain}`;
}

const SUPPORT_EMAIL = 'support@outdoorguides.com';

const EmailOtpSection = ({ email, onVerified }) => {
  const [otp, setOtp] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [resendLimitReached, setResendLimitReached] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const sendInitiated = useRef(false);

  useEffect(() => {
    if (!email) { return; } // wait until email prop is populated
    if (sendInitiated.current) { return; }
    sendInitiated.current = true;
    sendCode();
  }, [email]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (countdown <= 0) { return; }
    const id = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(id);
  }, [countdown]);

  const sendCode = async () => {
    try {
      await apiRequest('/api/v1/guides/signup/email-otp/send', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      setCountdown(60);
    } catch (err) {
      setError(err.message || 'Failed to send code. Please try again.');
    }
  };

  const handleSubmit = async () => {
    if (otp.length !== 6 || submitting) { return; }
    setSubmitting(true);
    setError('');
    try {
      await apiRequest('/api/v1/guides/signup/email-otp/verify', {
        method: 'POST',
        body: JSON.stringify({ code: otp }),
      });
      await apiRequest('/api/v1/guides/signup/progress', {
        method: 'PATCH',
        body: JSON.stringify({ signupStage: 2 }),
      });
      onVerified();
    } catch (err) {
      if (err.message && err.message.includes('429')) {
        setError('Too many incorrect attempts. Click Resend to get a new code.');
      } else {
        setError(err.message || 'Verification failed. Please try again.');
      }
      setOtp('');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setOtp('');
    try {
      await apiRequest('/api/v1/guides/signup/email-otp/resend', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      setCountdown(60);
    } catch (err) {
      if (err.message && err.message.includes('429')) {
        setResendLimitReached(true);
        setError('');
      } else {
        setError(err.message || 'Failed to resend code. Please try again.');
      }
    }
  };

  return (
    <div className="p-4 border border-gray-200 rounded-lg space-y-4">
      <div>
        <p className="font-semibold text-gray-900">Email Verification</p>
        <p className="text-sm text-gray-500">Code sent to {maskEmail(email)}</p>
      </div>

      {resendLimitReached ? (
        <p className="text-sm text-red-600" role="alert">
          Maximum resend attempts reached. Please{' '}
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="underline text-emerald-600 hover:text-emerald-700"
          >
            contact support
          </a>
          .
        </p>
      ) : (
        <>
          <OtpInputGroup
            value={otp}
            onChange={setOtp}
            disabled={submitting}
            inputPrefix="email-otp"
          />

          {error && (
            <p className="text-sm text-red-600" role="alert">{error}</p>
          )}

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={otp.length !== 6 || submitting}
              className="px-4 py-2 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? 'Verifying…' : 'Verify'}
            </button>
            <button
              type="button"
              onClick={handleResend}
              disabled={countdown > 0}
              className="text-sm text-emerald-600 hover:text-emerald-700 disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              {countdown > 0 ? `Resend in ${countdown}s` : 'Resend code'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default EmailOtpSection;
